import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { MOBILE_SEARCH, SMALL_MOBILE } from "common/constants";
import { RootState } from "app/store";
import TradesTable from "features/trades/TradesTable";
import React, { ReactNode, useContext, useMemo } from "react";
import TradesCardsList from "features/trades/TradesCards/TradesCardsList";
import { LanguageNameSpaces, TradeData, TradeDataWithKey } from "@shared/types";
import { checkTradeSearch } from "features/trades/common/tradeSearch";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useTableHeaderRow } from "common/Table/TableHeaderRow";
import useTableRowsSelector from "utils/hooks/useTableRowsSelector";
import { useTradesSort } from "@shared/features/trades/useTradesSort";
import TradesEmpty from "features/trades/TradesEmpty";
import { TradeActionsContext } from "@shared/features/trades/common/contexts";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context";
import { useTradeCard } from "features/trades/common/commonHooks";
import useMedia from "utils/hooks/useMedia.ts";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import styled, { useTheme } from "styled-components";
import Panel from "common/NewLook/Panel.tsx";
import NewLookTradesTableWrapper from "features/trades/NewLook/NewLookTradesTableWrapper.tsx";
import NewLookTradesCardsGrid from "features/trades/NewLook/NewLookTradesCardsGrid.tsx";
import useNewLookTradesCardsSort from "features/trades/NewLook/useNewLookTradesCardsSort.tsx";
import SectionHeader from "common/NewLook/SectionHeader.tsx";
import {
  NEW_LOOK_TRADES_LAYOUT_KEY,
  NEW_LOOK_TRADES_TABLE_MIN_VIEWPORT,
} from "features/trades/common/constants";
import { NEW_LOOK_CARDS_VIEW_MAX_WIDTH } from "common/NewLook/layout.ts";

interface TradesTableContentProps {
  dataSelector?: (state: RootState) => TradeData[] | undefined;
  data?: TradeData[];
  isOpenTrades: boolean;
  searchPlaceholder?: string;
  titleOrNode?: ReactNode;
  helpTooltip?: ReactNode;
  allowBulkActions?: boolean;
  hideActions?: boolean;
  openFilters?: () => void;
  actionTrigger?: ReactNode;
  allowTradeClick?: boolean;
  isCardView?: boolean;
  bulkActionTrades?: TradeData[];
}

const TradesTableContent = ({
  dataSelector,
  data: propsData,
  isOpenTrades,
  searchPlaceholder,
  titleOrNode,
  allowBulkActions,
  hideActions,
  helpTooltip,
  openFilters,
  actionTrigger,
  allowTradeClick,
  isCardView: propsIsCardView,
}: TradesTableContentProps) => {
  const { i18n } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { isDrawer } = useContext(DrawerContext) || {};
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const isNarrowScreen = useMedia(
    [`(max-width: ${NEW_LOOK_CARDS_VIEW_MAX_WIDTH})`],
    [true],
    false
  );
  const isTooNarrowForTable = useMedia(
    [`(max-width: ${NEW_LOOK_TRADES_TABLE_MIN_VIEWPORT})`],
    [true],
    false
  );
  const {
    isBacktesting,
    isLoading,
    isFetching,
    isUninitialized,
    precisionConfig,
    getTradesQuery,
    getOpenTradesQuery,
    filteredTradesData,
    bulkActionTrades,
  } = useContext(TradesPageContext)!;
  const {
    isLoadingBulkUpdate,
    selectedTradesIDs,
    setSelectedTradesIDs,
    getRowSelectable,
  } = useContext(TradeActionsContext) || {};

  const data = useSelector(
    dataSelector ||
      (() => {
        let data = propsData;
        if (isDrawer && !data) data = filteredTradesData;
        return data;
      })
  );

  const {
    amountChangeHeader,
    getTradeAmount,
    getTradeQuantity,
    openTradeExtraDetails,
  } = useTradeCard({ allowTradeClick });

  const { sortedData, ...sortData } = useTradesSort(data || [], isOpenTrades);
  const location = useLocation();

  const { isNewLook } = useTheme();
  const numOfItems = isOpenTrades
    ? sortedData?.length
    : getTradesQuery?.data?.aggregated_data?.number_of_closed_trades;
  // New look (page): the list is a panel, its title and search in the
  // panel header and the table edge to edge underneath.
  const useNewLookPanel =
    isNewLook && !isDrawer && typeof titleOrNode === "string";
  const { sortTrades, sortControl } = useNewLookTradesCardsSort(isOpenTrades);

  const { tableHeader, isCardsView, searchValue } = useTableHeaderRow({
    isDrawerTable: isDrawer,
    titleOrNode: useNewLookPanel ? undefined : titleOrNode,
    withSearch: true,
    searchPlaceholder,
    numOfItems: useNewLookPanel ? undefined : numOfItems,
    showLayoutChangeButton:
      !isDrawer && !(useNewLookPanel && isTooNarrowForTable),
    helpTooltip: useNewLookPanel ? undefined : helpTooltip,
    actionTrigger,
    layoutChangeKey: useNewLookPanel ? NEW_LOOK_TRADES_LAYOUT_KEY : undefined,
    defaultCardsView: useNewLookPanel ? isNarrowScreen : undefined,
  });

  const filteredData: TradeDataWithKey[] = useMemo(
    () =>
      sortedData
        ?.filter((tradeData) => checkTradeSearch(tradeData, searchValue))
        .map((tradeData) => ({
          ...tradeData,
          key: tradeData.id,
        })) || [],
    [sortedData, searchValue, i18n.language]
  );

  const showNewLookCards =
    useNewLookPanel && (isCardsView || isTooNarrowForTable);
  const sortedCardsData = useMemo(
    () => (showNewLookCards ? sortTrades(filteredData) : filteredData),
    [showNewLookCards, sortTrades, filteredData]
  );

  const { tableRowProps, pagination, handlePaginationChange } =
    useTableRowsSelector<TradeData, number>({
      selectedRows: selectedTradesIDs || [],
      setSelectedRows: setSelectedTradesIDs,
      getKey: (tradeData: TradeData) => tradeData.id,
      allRows: bulkActionTrades,
      filteredRows: filteredData,
      getRowSelectable,
    });

  const emptyState =
    !isUninitialized && data ? (
      <TradesEmpty
        isOpenTrades={isOpenTrades}
        iconWidth={isMobile || isDrawer ? 90 : 120}
        isSearch={!!searchValue?.length}
        openFilters={openFilters}
        filtersChipsOpen={SMALL_MOBILE}
        hasData={
          isOpenTrades ? !!getOpenTradesQuery?.data?.data?.length : undefined
        }
      />
    ) : undefined;

  const isLoadingWithBulk = isLoading || isLoadingBulkUpdate || isFetching;
  const tradesTable = useMemo(
    () => (
      <TradesTable
        data={filteredData}
        precisionConfig={precisionConfig}
        getTradeAmount={getTradeAmount}
        getTradeQuantity={getTradeQuantity}
        openTradeExtraDetails={openTradeExtraDetails}
        loading={isLoadingWithBulk}
        isOpenTrades={isOpenTrades}
        amountChangeHeader={amountChangeHeader}
        tableRowProps={allowBulkActions ? tableRowProps : undefined}
        pagination={pagination}
        handlePaginationChange={handlePaginationChange}
        hideActions={hideActions}
        sortData={sortData}
        emptyState={emptyState}
        allowTradeClick={allowTradeClick}
      />
    ),
    [
      filteredData,
      isOpenTrades,
      isLoadingWithBulk,
      amountChangeHeader,
      location.pathname,
      tableRowProps,
      pagination,
    ]
  );

  const tradesCardsList = (
    <TradesCardsList
      data={filteredData}
      loading={isLoadingWithBulk}
      isOpenTrades={isOpenTrades}
      emptyState={emptyState}
      allowTradeClick={allowTradeClick}
      shouldFetchSocketPnl={!isBacktesting}
    />
  );

  const content =
    propsIsCardView || isCardsView ? tradesCardsList : tradesTable;

  if (showNewLookCards) {
    return (
      <section>
        <SectionHeader
          title={titleOrNode}
          count={numOfItems || undefined}
          extra={helpTooltip}
          actions={
            <HeaderControls>
              {sortControl}
              {tableHeader}
            </HeaderControls>
          }
        />
        <NewLookTradesCardsGrid
          data={sortedCardsData}
          loading={isLoadingWithBulk}
          isOpenTrades={isOpenTrades}
          emptyState={emptyState}
          allowTradeClick={allowTradeClick}
          allowBulkActions={allowBulkActions}
          hideActions={hideActions}
          shouldFetchSocketPnl={!isBacktesting}
        />
      </section>
    );
  }

  if (useNewLookPanel) {
    return (
      <ListPanel
        title={titleOrNode}
        count={numOfItems || undefined}
        extra={helpTooltip}
        actions={<HeaderControls>{tableHeader}</HeaderControls>}
        flush={true}
      >
        <NewLookTradesTableWrapper $flush={true}>
          {content}
        </NewLookTradesTableWrapper>
      </ListPanel>
    );
  }

  return (
    <>
      {tableHeader}
      {isNewLook ? (
        <NewLookTradesTableWrapper>{content}</NewLookTradesTableWrapper>
      ) : (
        content
      )}
    </>
  );
};

export default TradesTableContent;

const ListPanel = styled(Panel)`
  overflow: hidden;
`;
/** The shared header row, reduced to its controls (search) in the panel. */
const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  > div {
    margin-bottom: 0 !important;
  }
`;
